import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { LOAN_TYPE_LABELS, Lead, LoanType, CONNECTOR_TYPE_LABELS } from "@/types";
import { INDIAN_CITIES, TEST_ADMIN_USER_ID, WORLD_COUNTRIES, INDIA_PHONE_INFO, getCountryPhoneInfo } from "@/lib/constants";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useLeads } from "@/hooks/useLeads";
import { useAuth } from "@/contexts/AuthContext";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { useBankers } from "@/hooks/useBankers";
import { useAppConfig } from "@/hooks/useAppConfig";
import { getAllActiveUsers } from "@/services/userService";
import { useModal } from "@/contexts/ModalContext";
import { Share2, ChevronDown, Users, User, CreditCard, Home, Briefcase, FileCheck, Building, Loader2, AlertCircle, MapPin, Map, ExternalLink, RotateCcw } from "lucide-react";
import { createLeadSchema, validateFormData, getFieldError, type CreateLeadFormData } from "@/lib/validations/leadValidation";
import { MapLocationPicker } from "@/components/common/MapLocationPicker";
import { 
  unformatPhoneNumber, 
  formatPANCard, 
  formatPincode, 
  formatCIBILScore,
  formatCurrencyInput,
  parseIndianCurrency,
  formatInterestRate,
  formatPercentage
} from "@/lib/utils/inputMasking";

type LeadCategory = "loan" | "insurance";

interface CreateLeadSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateLeadSheet({ open, onOpenChange }: CreateLeadSheetProps) {
  const { addLead } = useLeads();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { registerModal, unregisterModal } = useModal();
  const { partners: channelPartners = [] } = useChannelPartners();
  const { bankers, isFavorite: isBankerFavorite } = useBankers();
  const { config } = useAppConfig();
  const [category, setCategory] = useState<LeadCategory>("loan");
  const [formData, setFormData] = useState<Record<string, string | number | boolean | Date>>({});
  const [selectedColleagues, setSelectedColleagues] = useState<string[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [banks, setBanks] = useState<Array<{ id: string; name: string; logo?: string }>>([]);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id?: string; userId?: string; name: string }>>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Mobile number prefix/length follows the selected NRI country; defaults to India.
  const phoneInfo = formData.isNRI
    ? getCountryPhoneInfo(formData.nriCountry as string)
    : INDIA_PHONE_INFO;

  // Register with ModalContext so Android back button closes this sheet
  useEffect(() => {
    if (!open) return;
    registerModal('create-lead-sheet', () => onOpenChange(false), 1);
    return () => unregisterModal('create-lead-sheet');
  }, [open, onOpenChange, registerModal, unregisterModal]);

  // Reset location picker if sheet is closed while picker is still open
  useEffect(() => {
    if (!open) {
      setLocationPickerOpen(false);
    }
  }, [open]);

  // Load users from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        // Exclude app_user role and the current user from share list
        const filtered = usersData.filter(u =>
          u.primaryRole !== 'app_user' &&
          u.userId !== user?.id &&
          (u.userId || u.id) !== TEST_ADMIN_USER_ID
        );
        setAvailableUsers(filtered);
        console.log('Loaded', filtered.length, 'shareable users');
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, [user?.id]);

  // Convert config.banks to the format needed for the select
  useEffect(() => {
    if (config.banks && config.banks.length > 0) {
      const banksData = config.banks.map((bankName, index) => ({
        id: `bank-${index}`,
        name: bankName,
      }));
      setBanks(banksData);
      console.log('Loaded', banksData.length, 'banks from appConfig');
    }
  }, [config.banks]);

  const handleClearForm = () => {
    setFormData({});
    setLocation(null);
    setSelectedColleagues([]);
    setShareWithAll(false);
    setValidationErrors({});
    setCategory('loan');
    toast.info("Form cleared");
  };

  const handleToggleShareMember = (memberId: string) => {
    setSelectedColleagues(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSelectedColleagues([]);
      setShareWithAll(false);
    } else {
      setSelectedColleagues(availableUsers.map(m => m.userId || m.id));
      setShareWithAll(true);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setValidationErrors({});

    // NRI validation: require country of residence
    if (formData.isNRI && !formData.nriCountry) {
      toast.error("Please select the country of residence for NRI customer");
      setIsSubmitting(false);
      return;
    }

    // Prepare data for validation
    const fullNameRaw = ((formData.fullName as string) || '').trim();
    const nameParts = fullNameRaw.split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');

    const dataToValidate = {
      ...formData,
      firstName,
      lastName,
      mobile: unformatPhoneNumber((formData.mobile as string) || ''),
      secondaryNumber: formData.secondaryNumber ? unformatPhoneNumber(formData.secondaryNumber as string) : '',
      assignedTo: user?.id || 'u1',
      sharedWith: selectedColleagues,
      // When NRI: pass placeholder so customerCity validation passes; we clear it in actual save
      customerCity: formData.isNRI ? (formData.nriCountry || 'NRI') : formData.customerCity,
    };

    // Validate with Zod schema
    const validationResult = validateFormData(createLeadSchema, dataToValidate);
    
    if (!validationResult.success) {
      // Type assertion needed due to TypeScript control flow limitation
      const errors = (validationResult as { success: false; errors: Record<string, string> }).errors;
      console.warn('[CreateLeadSheet] Validation failed', {
        errors,
        data: dataToValidate,
      });
      setValidationErrors(errors);
      setIsSubmitting(false);
      toast.error("Please fix the validation errors");
      
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // After success check, validationResult is narrowed to success type
    const validatedData = validationResult.data;
    // NOTE: No phone uniqueness check for leads - same phone can be added by any user
    // Lead document IDs are always unique (auto-generated by Firestore)

    // NRI numbers are stored with their country's calling code prefixed (no
    // "+"), the same convention Call/WhatsApp already use elsewhere in the
    // app (e.g. "97150..."), so they're self-describing without a separate
    // country-code field. Domestic numbers stay as plain 10 digits.
    const dialCode = (formData.isNRI as boolean) ? getCountryPhoneInfo(formData.nriCountry as string).dialCode : '';
    const newLead = {
      category: category,
      firstName: firstName,
      lastName: lastName,
      mobile: dialCode ? `${dialCode}${validatedData.mobile}` : validatedData.mobile!,
      secondaryNumber: validatedData.secondaryNumber
        ? (dialCode ? `${dialCode}${validatedData.secondaryNumber}` : validatedData.secondaryNumber)
        : undefined,
      email: validatedData.email || undefined,
      dateOfBirth: validatedData.dateOfBirth || undefined,
      panCard: validatedData.panCard || undefined,
      pincode: validatedData.pincode || undefined,
      loanType: validatedData.loanType as LoanType,
      loanAmount: validatedData.loanAmount,
      loanTenure: validatedData.loanTenure,
      lender: validatedData.lender || undefined,
      customerCity: (formData.isNRI as boolean) ? undefined : (validatedData.customerCity || undefined),
      // NRI / Residency
      isNRI: !!(formData.isNRI as boolean) || false,
      nriCountry: (formData.isNRI as boolean) ? ((formData.nriCountry as string) || undefined) : undefined,
      // Property Details (Home Loan / LAP)
      isPropertyIdentified: !!(formData.isPropertyIdentified as boolean) || false,
      propertyType: (formData.propertyType as string) || undefined,
      propertyValue: formData.propertyValue ? Number(formData.propertyValue) : undefined,
      propertyPincode: (formData.propertyPincode as string) || undefined,
      propertyCity: (formData.propertyCity as string) || undefined,
      propertyAddress: (formData.propertyAddress as string) || undefined,
      status: 'new_lead' as const,
      employmentType: validatedData.employmentType,
      monthlySalary: validatedData.monthlySalary,
      cibilScore: validatedData.cibilScore,
      // BT & Top-Up fields
      isBT: validatedData.isBT || false,
      isTopUp: validatedData.isTopUp || false,
      existingLender: validatedData.existingLender || undefined,
      outstandingAmount: validatedData.outstandingAmount,
      currentInterestRate: validatedData.currentInterestRate,
      remainingTenure: validatedData.remainingTenure,
      topUpAmount: validatedData.topUpAmount,
      // Additional Employment/Business Info
      companyName: validatedData.companyName || undefined,
      yearsInCurrentJob: validatedData.yearsInCurrentJob,
      leadSource: validatedData.leadSource as Lead['leadSource'] | undefined,
      // Relationships
      connectorId: validatedData.connectorId || undefined,
      smContactId: validatedData.smContactId || undefined,
      // Assignment
      assignedTo: user?.id || 'u1',
      createdBy: user?.id || 'u1',
      officeId: user?.officeId,
      sharedWith: Array.from(new Set([TEST_ADMIN_USER_ID, ...selectedColleagues])),
      // Location
      location: location || undefined,
      timeline: [],
      checklist: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      // Race the addLead call against a 30-second timeout to prevent indefinite hang
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out after 30 seconds')), 30000)
      );
      const firestoreId = await Promise.race([addLead(newLead), timeoutPromise]);
      toast.success("Lead created successfully!");
      onOpenChange(false);
      setFormData({});
      setLocation(null);
      setSelectedColleagues([]);
      setShareWithAll(false);
      setValidationErrors({});
      
      // Navigate to the newly created lead detail page
      navigate(`/leads/${firestoreId}`);
    } catch (error) {
      console.error('Error creating lead:', error);
      const isTimeout = error instanceof Error && error.message.includes('timed out');
      toast.error(isTimeout
        ? "Request timed out. Please check your connection and try again."
        : "Failed to create lead. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateField = (field: string, value: string | number | boolean | Date) => {
    setFormData((prev) => ({ ...prev, [field]: value } as Record<string, string | number | boolean | Date>));
  };

  // Trim mobile/secondary numbers down if switching to a country with a shorter number length
  useEffect(() => {
    setFormData((prev) => {
      const mobile = (prev.mobile as string) || "";
      const secondary = (prev.secondaryNumber as string) || "";
      if (mobile.length <= phoneInfo.maxLength && secondary.length <= phoneInfo.maxLength) return prev;
      return {
        ...prev,
        mobile: mobile.slice(0, phoneInfo.maxLength),
        secondaryNumber: secondary.slice(0, phoneInfo.maxLength),
      };
    });
  }, [phoneInfo.maxLength]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
        <SheetHeader className="pb-4 px-4 flex-shrink-0">
          <div className="flex items-center justify-center gap-2">
            <SheetTitle className="text-xl font-bold">Add New Lead</SheetTitle>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleClearForm}
              className="h-8 w-8 rounded-lg shrink-0"
              title="Clear form"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-4">
        <div className="space-y-5 pb-8">
            {/* SECTION 1: CUSTOMER BASICS */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 pb-1">
                <User className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Customer Information</h3>
              </div>
              
              <div className="space-y-2">
                  <Label className="text-sm font-medium">Full Name <span className="text-red-500">*</span></Label>
                  <Input 
                    name="firstName"
                    placeholder="Ex - Rajesh Kumar"
                    value={(formData.fullName as string) || ""}
                    onChange={(e) => {
                      updateField("fullName", e.target.value);
                      if (validationErrors.firstName) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.firstName;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.firstName ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.firstName && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.firstName}</span>
                    </div>
                  )}
                </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Mobile Number <span className="text-red-500">*</span></Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+{phoneInfo.dialCode || "--"}</span>
                  <Input
                    type="tel"
                    name="mobile"
                    placeholder={`Enter ${phoneInfo.minLength === phoneInfo.maxLength ? `${phoneInfo.maxLength}-digit` : `${phoneInfo.minLength}-${phoneInfo.maxLength} digit`} mobile number`}
                    maxLength={phoneInfo.maxLength}
                    value={(formData.mobile as string) || ""}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, phoneInfo.maxLength);
                      updateField("mobile", digits);
                      // Clear error when user types
                      if (validationErrors.mobile) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.mobile;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 rounded-l-none placeholder:text-muted-foreground/50 ${validationErrors.mobile ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                </div>
                {validationErrors.mobile && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.mobile}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Alternate Number</Label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+{phoneInfo.dialCode || "--"}</span>
                  <Input
                    name="secondaryNumber"
                    type="tel"
                    maxLength={phoneInfo.maxLength}
                    placeholder={`Enter ${phoneInfo.minLength === phoneInfo.maxLength ? `${phoneInfo.maxLength}-digit` : `${phoneInfo.minLength}-${phoneInfo.maxLength} digit`} mobile number`}
                    value={(formData.secondaryNumber as string) || ""}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, phoneInfo.maxLength);
                      updateField("secondaryNumber", digits);
                      if (validationErrors.secondaryNumber) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.secondaryNumber;
                          return newErrors;
                      });
                    }
                  }}
                    className={`h-12 rounded-l-none placeholder:text-muted-foreground/50 ${validationErrors.secondaryNumber ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                </div>
                {validationErrors.secondaryNumber && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.secondaryNumber}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Email (Optional)</Label>
                <Input 
                  name="email"
                  type="email" 
                  placeholder="name@example.com"
                  value={(formData.email as string) || ""}
                  onChange={(e) => {
                    updateField("email", e.target.value);
                    if (validationErrors.email) {
                      setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.email;
                        return newErrors;
                      });
                    }
                  }}
                  className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {validationErrors.email && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.email}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Date of Birth</Label>
                <Input 
                  type="date"
                  value={formData.dateOfBirth instanceof Date ? formData.dateOfBirth.toISOString().split('T')[0] : (formData.dateOfBirth as string) || ""}
                  onChange={(e) => updateField("dateOfBirth", e.target.value)}
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Customer Location</Label>
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={() => setLocationPickerOpen(true)}
                    >
                      <Map className="h-4 w-4 mr-2 shrink-0" />
                      {location ? "Location Set \u2713 \u2014 Change" : "Pick Location on Map"}
                    </Button>
                  </div>
                  {location && (
                    <p className="text-xs text-muted-foreground pl-1">📍 {location.lat.toFixed(6)}, {location.lng.toFixed(6)}</p>
                  )}
                </div>
              </div>

              {/* NRI / Indian Resident Toggle */}
              <div className="space-y-2">
                <div className="flex items-center justify-between py-1">
                  <div>
                    <Label className="text-sm font-medium">Customer is NRI?</Label>
                    <p className="text-xs text-muted-foreground">Toggle ON if customer is Non-Resident Indian</p>
                  </div>
                  <Switch
                    checked={!!(formData.isNRI as boolean)}
                    onCheckedChange={(v) => updateField("isNRI", v)}
                  />
                </div>
              </div>

              {/* Customer City + Pincode (Indian Resident only) */}
              {!formData.isNRI && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Customer City <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={INDIAN_CITIES.map(city => ({ value: city, label: city }))}
                    value={(formData.customerCity as string) || ""}
                    onValueChange={(value) => {
                      updateField("customerCity", value);
                      if (validationErrors.customerCity) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.customerCity;
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Select city"
                    searchPlaceholder="Search cities..."
                    emptyText="No city found."
                    className="h-12"
                  />
                  {validationErrors.customerCity && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.customerCity}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Customer Pincode</Label>
                  <Input 
                    name="pincode"
                    placeholder="Ex - 400001"
                    maxLength={6}
                    inputMode="numeric"
                    value={(formData.pincode as string) || ""}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                      updateField("pincode", digits);
                      if (validationErrors.pincode) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.pincode;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.pincode ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.pincode && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.pincode}</span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* NRI Country Picker (NRI only) */}
              {!!formData.isNRI && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Country of Residence <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={WORLD_COUNTRIES.map(c => ({ value: c, label: c }))}
                    value={(formData.nriCountry as string) || ""}
                    onValueChange={(value) => updateField("nriCountry", value)}
                    placeholder="Select country"
                    searchPlaceholder="Search countries..."
                    emptyText="No country found."
                    className="h-12"
                  />
                </div>
              )}
            </div>

            {/* SECTION 2: LOAN REQUIREMENT */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 pb-1">
                <CreditCard className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Loan Requirement</h3>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Loan Type <span className="text-red-500">*</span></Label>
                <Select value={(formData.loanType as string) || ""} onValueChange={(v) => {
                  updateField("loanType", v);
                  if (validationErrors.loanType) {
                    setValidationErrors(prev => { const e = {...prev}; delete e.loanType; return e; });
                  }
                }}>
                  <SelectTrigger className={`h-12 ${validationErrors.loanType ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select loan type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LOAN_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.loanType && (
                  <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                    <AlertCircle className="h-3 w-3" />
                    <span>{validationErrors.loanType}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Loan Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input 
                    name="loanAmount"
                    type="text" 
                    inputMode="numeric"
                    placeholder="Ex - 50,00,000"
                    value={formatCurrencyInput((formData.loanAmount as string) || "")}
                    onChange={(e) => {
                      updateField("loanAmount", e.target.value.replace(/,/g, ''));
                      if (validationErrors.loanAmount) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.loanAmount;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.loanAmount ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.loanAmount && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.loanAmount}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Tenure (Years) <span className="text-red-500">*</span></Label>
                  <Input 
                    name="loanTenure"
                    type="number" 
                    placeholder="Ex - 20"
                    value={(formData.loanTenure as string | number) || ""}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onChange={(e) => {
                      updateField("loanTenure", e.target.value);
                      if (validationErrors.loanTenure) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.loanTenure;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.loanTenure ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.loanTenure && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.loanTenure}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* BT & Top-Up Toggles */}
              <div className="grid grid-cols-2 gap-3">
                <div 
                  className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                  style={{
                    backgroundColor: formData.isBT ? 'rgb(254 252 232)' : 'transparent',
                    borderColor: formData.isBT ? 'rgb(251 191 36)' : 'rgb(226 232 240)'
                  }}
                  onClick={() => updateField("isBT", !formData.isBT)}
                >
                  <Checkbox 
                    id="isBT"
                    checked={(formData.isBT as boolean) || false}
                    onCheckedChange={(checked) => updateField("isBT", checked)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="isBT" className="text-sm font-semibold cursor-pointer block">
                      BT Case
                    </Label>
                    <p className="text-xs text-muted-foreground">Balance Transfer</p>
                  </div>
                </div>
                
                <div 
                  className="flex items-center space-x-2 p-3 border-2 rounded-xl cursor-pointer transition-all hover:bg-muted/50"
                  style={{
                    backgroundColor: formData.isTopUp ? 'rgb(240 253 244)' : 'transparent',
                    borderColor: formData.isTopUp ? 'rgb(34 197 94)' : 'rgb(226 232 240)'
                  }}
                  onClick={() => updateField("isTopUp", !formData.isTopUp)}
                >
                  <Checkbox 
                    id="isTopUp"
                    checked={(formData.isTopUp as boolean) || false}
                    onCheckedChange={(checked) => updateField("isTopUp", checked)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="isTopUp" className="text-sm font-semibold cursor-pointer block">
                      Top-Up Case
                    </Label>
                    <p className="text-xs text-muted-foreground">Additional loan</p>
                  </div>
                </div>
              </div>

              {/* BT Case Details */}
              {formData.isBT && (
                <div className="p-4 border-2 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="h-1 w-1 rounded-full bg-amber-600"></div>
                    <Label className="text-sm font-semibold text-amber-900">Existing Loan Details</Label>
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-amber-800">Current Lender <span className="text-red-500">*</span></Label>
                      <SearchableSelect
                        options={banks.map(bank => ({ value: bank.name, label: bank.name }))}
                        value={(formData.existingLender as string) || ""}
                        onValueChange={(v) => {
                          updateField("existingLender", v);
                          if (validationErrors.existingLender) {
                            setValidationErrors(prev => { const e = {...prev}; delete e.existingLender; return e; });
                          }
                        }}
                        placeholder="Select current lender"
                        searchPlaceholder="Search lender..."
                        className={validationErrors.existingLender ? 'border-red-500' : ''}
                      />
                      {validationErrors.existingLender && (
                        <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                          <AlertCircle className="h-3 w-3" />
                          <span>{validationErrors.existingLender}</span>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-amber-800">Outstanding (₹) <span className="text-red-500">*</span></Label>
                        <Input 
                          name="outstandingAmount"
                          type="text"
                          inputMode="numeric"
                          placeholder="Ex - 25,00,000"
                          value={formatCurrencyInput((formData.outstandingAmount as string) || "")}
                          onChange={(e) => {
                            updateField("outstandingAmount", e.target.value.replace(/,/g, ''));
                            if (validationErrors.outstandingAmount) {
                              setValidationErrors(prev => { const e = {...prev}; delete e.outstandingAmount; return e; });
                            }
                          }}
                          className={`h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50 ${validationErrors.outstandingAmount ? 'border-red-500' : ''}`}
                        />
                        {validationErrors.outstandingAmount && (
                          <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                            <AlertCircle className="h-3 w-3" />
                            <span>{validationErrors.outstandingAmount}</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-amber-800">Current ROI (%)</Label>
                        <Input 
                          type="number"
                          step="0.1"
                          placeholder="Ex - 9.5"
                          value={(formData.currentInterestRate as string | number) || ""}
                          onWheel={(e) => (e.target as HTMLElement).blur()}
                          onChange={(e) => updateField("currentInterestRate", e.target.value)}
                          className="h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-amber-800">Remaining Tenure (Years)</Label>
                      <Input 
                        type="number"
                        step="0.5"
                        placeholder="Ex - 15"
                        value={(formData.remainingTenure as string | number) || ""}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onChange={(e) => updateField("remainingTenure", e.target.value)}
                        className="h-11 bg-white border-amber-200 focus:border-amber-400 placeholder:text-muted-foreground/50"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Top-Up Amount */}
              {formData.isTopUp && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Top-Up Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input 
                    name="topUpAmount"
                    type="text"
                    inputMode="numeric"
                    placeholder="Ex - 5,00,000"
                    value={formatCurrencyInput((formData.topUpAmount as string) || "")}
                    onChange={(e) => {
                      updateField("topUpAmount", e.target.value.replace(/,/g, ''));
                      if (validationErrors.topUpAmount) {
                        setValidationErrors(prev => { const e = {...prev}; delete e.topUpAmount; return e; });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.topUpAmount ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.topUpAmount && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.topUpAmount}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SECTION 3: PROPERTY DETAILS (for home/LAP) */}
            {(['home_loan', 'lap'].includes(formData.loanType as string)) && (
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-2 pb-1">
                  <Home className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Property Details</h3>
                </div>

                {/* Property Identified Toggle */}
                <div className="flex items-center justify-between py-1 bg-muted/30 rounded-lg px-3">
                  <div>
                    <Label className="text-sm font-medium">Is Property Identified?</Label>
                    <p className="text-xs text-muted-foreground">Turn ON if the property has been shortlisted</p>
                  </div>
                  <Switch
                    checked={!!(formData.isPropertyIdentified as boolean)}
                    onCheckedChange={(v) => updateField("isPropertyIdentified", v)}
                  />
                </div>

                {/* Rest of property fields — only shown when property is identified */}
                {!!formData.isPropertyIdentified && (
                <>
                {/* Property Type */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Type</Label>
                  <Select value={(formData.propertyType as string) || ""} onValueChange={(v) => updateField("propertyType", v)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartment">Apartment / Flat</SelectItem>
                      <SelectItem value="independent_house">Independent House / Villa</SelectItem>
                      <SelectItem value="plot">Plot / Land</SelectItem>
                      <SelectItem value="commercial">Commercial Property</SelectItem>
                      <SelectItem value="under_construction">Under Construction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Value + Property Pincode */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Property Value (₹)</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Ex - 75,00,000"
                      value={formatCurrencyInput((formData.propertyValue as string) || "")}
                      onChange={(e) => updateField("propertyValue", e.target.value.replace(/,/g, ''))}
                      className="h-12 placeholder:text-muted-foreground/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Property Pincode</Label>
                    <Input
                      placeholder="Ex - 400001"
                      maxLength={6}
                      inputMode="numeric"
                      value={(formData.propertyPincode as string) || ""}
                      onChange={(e) => updateField("propertyPincode", e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-12 placeholder:text-muted-foreground/50"
                    />
                  </div>
                </div>

                {/* Property City */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property City</Label>
                  <SearchableSelect
                    options={INDIAN_CITIES.map(city => ({ value: city, label: city }))}
                    value={(formData.propertyCity as string) || ""}
                    onValueChange={(v) => updateField("propertyCity", v)}
                    placeholder="Select city"
                    searchPlaceholder="Search cities..."
                    emptyText="No city found."
                    className="h-12"
                  />
                </div>

                {/* Property Address */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Property Address</Label>
                  <Input
                    placeholder="Building, street, area…"
                    value={(formData.propertyAddress as string) || ""}
                    onChange={(e) => updateField("propertyAddress", e.target.value)}
                    className="h-12 placeholder:text-muted-foreground/50"
                  />
                </div>
                </>
                )}
              </div>
            )}

            {/* SECTION 4: EMPLOYMENT & INCOME */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 pb-1">
                <Briefcase className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Employment & Income</h3>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Employment Type</Label>
                <Select value={(formData.employmentType as string) || ""} onValueChange={(v) => updateField("employmentType", v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select employment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salaried">Salaried</SelectItem>
                    <SelectItem value="self_employed_itr">Self Employed (ITR)</SelectItem>
                    <SelectItem value="self_employed_pro">Self Employed Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Monthly Income</Label>
                  <Input 
                    name="monthlySalary"
                    type="text" 
                    inputMode="numeric"
                    placeholder="Ex - 50,000"
                    value={formatCurrencyInput((formData.monthlySalary as string) || "")}
                    onChange={(e) => {
                      updateField("monthlySalary", e.target.value.replace(/,/g, ''));
                      if (validationErrors.monthlySalary) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.monthlySalary;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.monthlySalary ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.monthlySalary && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.monthlySalary}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Years in Job/Business</Label>
                  <Input 
                    name="yearsInCurrentJob"
                    type="number"
                    step="0.5"
                    placeholder="Ex - 2.5"
                    value={(formData.yearsInCurrentJob as string | number) || ""}
                    onWheel={(e) => (e.target as HTMLElement).blur()}
                    onChange={(e) => {
                      updateField("yearsInCurrentJob", e.target.value);
                      if (validationErrors.yearsInCurrentJob) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.yearsInCurrentJob;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.yearsInCurrentJob ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.yearsInCurrentJob && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.yearsInCurrentJob}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Company / Business Name</Label>
                <Input 
                  placeholder="Ex - TCS / Sharma Enterprises"
                  value={(formData.companyName as string) || ""}
                  onChange={(e) => updateField("companyName", e.target.value)}
                  className="h-12 placeholder:text-muted-foreground/50"
                />
              </div>
            </div>

            {/* SECTION 5: CREDIT PROFILE */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 pb-1">
                <FileCheck className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Credit Profile</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">PAN Card</Label>
                  <Input 
                    name="panCard"
                    placeholder="Ex - ABCDE1234F"
                    maxLength={10}
                    value={formatPANCard((formData.panCard as string) || "")}
                    onChange={(e) => {
                      const formatted = formatPANCard(e.target.value);
                      updateField("panCard", formatted);
                      if (validationErrors.panCard) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.panCard;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.panCard ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.panCard && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.panCard}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">CIBIL Score</Label>
                  <Input 
                    name="cibilScore"
                    placeholder="Ex - 750"
                    maxLength={3}
                    value={(formData.cibilScore as string | number) || ""}
                    onChange={(e) => {
                      const formatted = formatCIBILScore(e.target.value);
                      updateField("cibilScore", formatted);
                      if (validationErrors.cibilScore) {
                        setValidationErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors.cibilScore;
                          return newErrors;
                        });
                      }
                    }}
                    className={`h-12 placeholder:text-muted-foreground/50 ${validationErrors.cibilScore ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  {validationErrors.cibilScore && (
                    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>{validationErrors.cibilScore}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 6: BANK & PARTNERSHIP */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center gap-2 pb-1">
                <Building className="h-4 w-4 text-green-600" />
                <h3 className="font-semibold text-green-800">Processing Details</h3>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Preferred Lender</Label>
                <SearchableSelect
                  options={banks.map(bank => ({ value: bank.name, label: bank.name }))}
                  value={(formData.lender as string) || ""}
                  onValueChange={(v) => updateField("lender", v)}
                  placeholder="Select lender"
                  searchPlaceholder="Search banks..."
                  emptyText="No bank found."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Lead Source</Label>
                <Select value={(formData.leadSource as string) || ""} onValueChange={(v) => updateField("leadSource", v)}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="How did this lead come?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct (Walk-in)</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="channel_partner">Channel Partner</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Channel Partner (Optional)</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "None" },
                    ...[...channelPartners]
                      .sort((a, b) => {
                        if (a.isFavorite && !b.isFavorite) return -1;
                        if (!a.isFavorite && b.isFavorite) return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(partner => ({
                        value: partner.id,
                        label: `${partner.isFavorite ? '★ ' : ''}${partner.name} (${CONNECTOR_TYPE_LABELS[partner.type]}) - ${partner.city}`
                      }))
                  ]}
                  value={(formData.connectorId as string) || ""}
                  onValueChange={(v) => updateField("connectorId", v === "none" ? "" : v)}
                  placeholder="Select channel partner"
                  searchPlaceholder="Search partners..."
                  emptyText="No partner found."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Banker Contact (Optional)</Label>
                <SearchableSelect
                  options={[
                    { value: "none", label: "None" },
                    ...[...bankers]
                      .sort((a, b) => {
                        const aFav = isBankerFavorite(a.id);
                        const bFav = isBankerFavorite(b.id);
                        if (aFav && !bFav) return -1;
                        if (!aFav && bFav) return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(banker => ({
                        value: banker.id,
                        label: `${isBankerFavorite(banker.id) ? '★ ' : ''}${banker.name} - ${banker.bank}`,
                        displayLabel: banker.name
                      }))
                  ]}
                  value={(formData.smContactId as string) || ""}
                  onValueChange={(v) => updateField("smContactId", v === "none" ? "" : v)}
                  placeholder="Select banker contact"
                  searchPlaceholder="Search bankers..."
                  emptyText="No banker found."
                />
              </div>
            </div>

            {/* Share with Colleagues */}
            <div className="pt-4 border-t">
              <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" type="button">
                    <span className="flex items-center gap-2">
                      <Share2 className="h-4 w-4" />
                      <span>
                        {selectedColleagues.length > 0 
                          ? `Sharing with ${shareWithAll ? 'all' : selectedColleagues.length} colleague${selectedColleagues.length > 1 ? 's' : ''}`
                          : 'Share with colleagues'
                        }
                      </span>
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${shareOpen ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-3 space-y-2">
                  <div 
                    className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                    onClick={handleShareWithAll}
                  >
                    <Checkbox checked={shareWithAll} onCheckedChange={handleShareWithAll} />
                    <div className="flex-1">
                      <span className="text-sm font-medium">Share with all</span>
                    </div>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {availableUsers.map((member, index) => (
                      <div 
                        key={member.userId || member.id || `user-${index}`}
                        className="flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                        onClick={() => handleToggleShareMember(member.userId || member.id)}
                      >
                        <Checkbox 
                          checked={selectedColleagues.includes(member.userId || member.id)} 
                          onCheckedChange={() => handleToggleShareMember(member.userId || member.id)}
                        />
                        <span className="text-sm truncate">{member.name.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button 
                onClick={handleSubmit} 
                disabled={isSubmitting}
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Lead...
                  </>
                ) : (
                  'Create Lead'
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>

      <MapLocationPicker
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        initialLocation={location}
        onLocationSelect={(loc) => {
          setLocation(loc);
          setLocationPickerOpen(false);
        }}
      />
    </Sheet>
  );
}
